import BarberDefaultImage from '@assets/images/barber-default.png';
import FormContainer from '@components/FormContainer';
import Colors from '@constants/Colors';
import JobOfferProfileForm from '@modules/JobOffers/forms/JobOfferProfileForm';
import { Box, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

const CompanyLogoContainer = styled(Box)(() => ({
    backgroundColor: Colors.PRIMARY_PURPLE_30,
    borderRadius: '12px',
    padding: '10px',
    width: '132px',
    height: '132px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const CompanyLogo = styled('img')(() => ({
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    objectFit: 'cover'
}));

const CategoryChip = styled(Chip)(() => ({
    backgroundColor: 'rgba(60, 45, 225, 0.3)',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '16px',
    '& .MuiChip-label': {
        padding: '8px 12px'
    }
}));

const LocationChip = styled(Chip)(() => ({
    backgroundColor: Colors.OTHERS_GOLD,
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '16px',
    '& .MuiChip-label': {
        padding: '8px 12px'
    }
}));

const BenefitChip = styled(Chip)(() => ({
    backgroundColor: Colors.DISABLE_FORM_BLACK,
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '16px',
    '& .MuiChip-label': {
        padding: '12px 23px'
    }
}));

const JobOfferTitle = ({ pro, jobOffer, onSubmit, loading }: { pro: any, jobOffer: any, onSubmit?: () => Promise<void>, loading?: boolean }) => {
    const [formState, setFormState] = useState<'view' | 'edit'>('view');
    const { getValues } = useFormContext();

    const handleCancel = () => {
        setFormState('view');
    };

    const handleSubmit = async () => {
        await onSubmit?.();
        setFormState('view');
    }

    // Extract company data from jobOffer
    const companyName = jobOffer?.brand?.brandName || pro?.commercialName || '';
    const companyLogo = jobOffer?.brand?.brandLogo?.url || pro?.logo || BarberDefaultImage;
    const category = jobOffer?.brand?.sector?.sectorName || pro?.sector?.sectorName || '';
    const location = getValues('city') || pro?.city || '';
    const jobTitle = getValues('title') || '';
    
    return (
        <FormContainer
            formTitle="Titre de l'offre"
            formState={formState}
            setFormState={setFormState}
            loading={loading}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
        >
            {formState === "view" ? (<Box display="flex" gap="24px" alignItems="flex-start">
                {/* Company Logo */}
                <CompanyLogoContainer>
                    <CompanyLogo
                        src={companyLogo}
                        alt={`${companyName} logo`}
                    />
                </CompanyLogoContainer>

                {/* Main Content */}
                <Box flex="1" display="flex" flexDirection="column" gap="8px">
                    {/* Company Name and Tags */}
                    <Box display="flex" alignItems="center" gap="12px">
                        <Typography
                            variant="h6"
                            sx={{
                                color: Colors.PRIMARY_PURPLE_10,
                                fontWeight: 700,
                                fontSize: '16px'
                            }}
                        >
                            {companyName}
                        </Typography>
                        {category && (
                            <CategoryChip
                                label={category}
                                size="small"
                            />
                        )}
                        {location && (
                            <LocationChip
                                label={location}
                                size="small"
                            />
                        )}
                    </Box>

                    {/* Job Title */}
                    <Typography
                        variant="h5"
                        sx={{
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '24px',
                            lineHeight: 'normal',
                            fontFamily: 'LEMON_MILK_Pro, sans-serif'
                        }}
                    >
                        {jobTitle}
                    </Typography>
                    {/* Benefits */}
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap="16px"
                        sx={{
                            overflow: 'hidden',
                            maxWidth: '100%'
                        }}
                    >
                        {getValues('contract.benefits')?.map((benefit, index) => (
                            <BenefitChip
                                key={index}
                                label={benefit}
                                size="small"
                            />
                        ))}
                    </Box>
                </Box>
            </Box>) : <>
                <JobOfferProfileForm pro={pro} />
            </>}
        </FormContainer>
    );
};

export default JobOfferTitle;
